import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Button,
  Grid,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import InitialNodeCard from './InitialNodeCard';
import InitialQuestionnaireCard from './InitialQuestionnaireCard';
import { NodeTypeSelector } from '../ForumMode/core/NodeTypeSelector';
import { getNodeType } from '../../utils/nodeTypeUtils';

/**
 * 引導單元群組容器組件
 * @param {Object} group - 群組資料
 * @param {number} index - 群組索引
 * @param {Function} onUpdateGroup - 更新群組資訊
 * @param {Function} onRemoveGroup - 刪除群組
 * @param {Function} onAddNode - 新增節點到此群組
 * @param {Function} onUpdateNode - 更新節點
 * @param {Function} onRemoveNode - 刪除節點
 * @param {Object} nodePlan - 節點計劃配置
 */
const InitialNodeGroup = ({
  group,
  index,
  onUpdateGroup,
  onRemoveGroup,
  onAddNode,
  onUpdateNode,
  onRemoveNode,
  nodePlan,
  compact = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(group.name);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editDesc, setEditDesc] = useState(group.description || '');
  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);

  const handleSaveName = () => {
    onUpdateGroup(group.id, { name: editName });
    setIsEditingName(false);
  };

  const handleCancelName = () => {
    setEditName(group.name);
    setIsEditingName(false);
  };

  const handleSaveDesc = () => {
    onUpdateGroup(group.id, { description: editDesc });
    setIsEditingDesc(false);
  };

  const handleCancelDesc = () => {
    setEditDesc(group.description || '');
    setIsEditingDesc(false);
  };

  return (
    <Draggable draggableId={group.id} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={snapshot.isDragging ? 6 : 2}
          sx={{
            mb: 2,
            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
            transition: 'background-color 0.2s ease',
          }}
        >
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                },
              }}
            >
              <Box
                {...provided.dragHandleProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                }}
              >
                <DragIndicatorIcon color="action" />
              </Box>

              {isEditingName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }} onClick={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelName();
                    }}
                    autoFocus
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton size="small" onClick={handleSaveName} color="primary">
                    <CheckIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelName}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                  {compact && (
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#6c5ce7', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <EventNoteIcon fontSize="small" />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h6">{compact ? `${index + 1}. ${group.name}` : group.name}</Typography>
                    {compact && (
                      <Typography variant="caption" color="text.secondary">
                        {group.nodes.length} 個任務
                      </Typography>
                    )}
                  </Box>
                  {!group.isDefault && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingName(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              <Chip
                label={group.timing === 'immediate' ? '立即顯示' : '手動添加'}
                color={group.timing === 'immediate' ? 'primary' : 'default'}
                size="small"
              />
              
              {group.isDefault && (
                <Chip label="預設群組" color="info" size="small" variant="outlined" />
              )}

              {compact && (
                <>
                  <IconButton
                    size="small"
                    aria-label="更多"
                    onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!group.isDefault && (
                      <MenuItem onClick={() => { setMenuAnchor(null); setIsEditingName(true); }}>重新命名</MenuItem>
                    )}
                    <MenuItem onClick={() => { setMenuAnchor(null); setTypeSelectorOpen(true); }}>新增任務</MenuItem>
                    {!group.isDefault && (
                      <MenuItem
                        sx={{ color: 'error.main' }}
                        onClick={() => {
                          setMenuAnchor(null);
                          if (window.confirm(`確定要刪除群組「${group.name}」嗎？`)) onRemoveGroup(group.id);
                        }}
                      >
                        刪除活動
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}

              {!compact && !group.isDefault && (
                <Tooltip title="刪除群組">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`確定要刪除群組「${group.name}」嗎？`)) {
                        onRemoveGroup(group.id);
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                {/* 群組描述 */}
                <Box sx={{ mb: 2 }}>
                  {isEditingDesc ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        placeholder="群組描述"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="contained" onClick={handleSaveDesc}>
                          儲存
                        </Button>
                        <Button size="small" variant="outlined" onClick={handleCancelDesc}>
                          取消
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                        p: 1,
                        borderRadius: 1,
                      }}
                      onClick={() => setIsEditingDesc(true)}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {group.description || '點擊添加描述...'}
                      </Typography>
                      <EditIcon fontSize="small" color="action" />
                    </Box>
                  )}
                </Box>

                {/* 節點列表 */}
                <Droppable droppableId={group.id} type="node">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        minHeight: group.nodes.length === 0 ? 100 : 'auto',
                        backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                        border: snapshot.isDraggingOver ? '2px dashed' : '2px dashed transparent',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        p: 1,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {group.nodes.length === 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 100,
                            color: 'text.secondary',
                          }}
                        >
                          <Typography variant="body2">
                            拖曳節點到這裡或點擊下方按鈕新增節點
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {group.nodes.map((node, nodeIndex) => (
                            <Draggable key={node.id} draggableId={node.id} index={nodeIndex}>
                              {(provided, snapshot) => (
                                <Grid
                                  item
                                  xs={12}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <Box
                                    sx={{
                                      opacity: snapshot.isDragging ? 0.5 : 1,
                                      position: 'relative',
                                    }}
                                  >
                                    <Box
                                      {...provided.dragHandleProps}
                                      sx={{
                                        position: 'absolute',
                                        left: -8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        cursor: 'grab',
                                        zIndex: 1,
                                        '&:active': { cursor: 'grabbing' },
                                      }}
                                    >
                                      <DragIndicatorIcon color="action" />
                                    </Box>
                                    {/* switch-case pattern for extensibility: add new node types here */}
                                    {(() => {
                                      const commonProps = {
                                        node,
                                        index: nodeIndex,
                                        onTitleChange: (_, title) =>
                                          onUpdateNode(group.id, node.id, { title }),
                                        onContentChange: (_, content) =>
                                          onUpdateNode(group.id, node.id, { content }),
                                        onRemove: () => onRemoveNode(group.id, node.id),
                        onAddAfter: (afterId) => onAddNode(group.id, { insertAfter: afterId }),
                                      };
                                      switch (getNodeType(node)) {
                                        case 'questionnaire':
                                          return <InitialQuestionnaireCard {...commonProps} />;
                                        default:
                                          return <InitialNodeCard {...commonProps} nodePlan={nodePlan} compact={compact} />;
                                      }
                                    })()}
                                  </Box>
                                </Grid>
                              )}
                            </Draggable>
                          ))}
                        </Grid>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>

                {/* 新增節點按鈕 */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setTypeSelectorOpen(true)}
                  >
                    {compact ? '新增任務' : '新增內容到此單元'}
                  </Button>
                </Box>

                <NodeTypeSelector
                  open={typeSelectorOpen}
                  onClose={() => setTypeSelectorOpen(false)}
                  onSelect={(type) => {
                    if (type === 'questionnaire') {
                      onAddNode(group.id, {
                        type: 'questionnaire',
                        tags: 'questionnaire',
                        content: JSON.stringify({ description: '', questions: [{ id: 1, text: '' }] }),
                      });
                    } else {
                      onAddNode(group.id);
                    }
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}
    </Draggable>
  );
};

export default InitialNodeGroup;
